// Admin-list name rendering helpers.
//
// Admin pages (Manage Users, Entries Ledger) display the user's LEGAL name as
// the primary identifier. When the user has opted in to a display name and
// any portion of it differs from the legal name, we render that display name
// as a supplement underneath. Empty / equal display names are suppressed —
// rendering "Andrew Smith / Andrew Smith" would be noise.
//
// Comparison is case-insensitive and trims whitespace so casual differences
// ("Bob " vs "Bob") don't cause a redundant supplement to appear.

interface NameLike {
  first_name?: string | null;
  last_name?: string | null;
  display_first_name?: string | null;
  display_last_name?: string | null;
}

interface LedgerLike {
  legal_first_name?: string | null;
  legal_last_name?: string | null;
  display_first_name?: string | null;
  display_last_name?: string | null;
}

function normalize(s?: string | null): string {
  return (s ?? '').trim().toLowerCase();
}

function joinName(first?: string | null, last?: string | null): string {
  return [first, last].map((s) => (s ?? '').trim()).filter(Boolean).join(' ');
}

/**
 * Returns the display-name supplement string for an admin list row, or null
 * if no supplement should be rendered. A supplement is shown when at least
 * one of display_first_name / display_last_name is populated AND the full
 * display name differs from the full legal name (case-insensitive). Partial
 * differences are handled — if only display_first_name is set we render
 * just that piece against the corresponding legal first name.
 */
export function getDisplayNameSupplement(
  source: NameLike | LedgerLike,
): string | null {
  // Extract from either shape — ManageUsers uses first_name/last_name,
  // EntriesLedger uses legal_first_name/legal_last_name.
  const legalFirst =
    (source as LedgerLike).legal_first_name ??
    (source as NameLike).first_name ??
    null;
  const legalLast =
    (source as LedgerLike).legal_last_name ??
    (source as NameLike).last_name ??
    null;
  const displayFirst = source.display_first_name ?? null;
  const displayLast = source.display_last_name ?? null;

  // Nothing opted-in → no supplement.
  if (!displayFirst && !displayLast) return null;

  // Build the supplement by piece: fall back to the legal counterpart for
  // any half that's null so "(Smith)" never appears alone next to a missing
  // first name — instead we render the full name with the supplied piece
  // substituted in.
  const supFirst = (displayFirst ?? legalFirst) ?? null;
  const supLast = (displayLast ?? legalLast) ?? null;
  const supplementFull = joinName(supFirst, supLast);
  const legalFull = joinName(legalFirst, legalLast);

  if (!supplementFull) return null;
  // Suppress when display equals legal (case/whitespace-insensitive).
  if (normalize(supplementFull) === normalize(legalFull)) return null;

  return supplementFull;
}
